// Module-level journal of inline cell edits, keyed by submission. Each cell
// keeps its FIRST original value across repeated edits, so the journal always describes
// "file value → current value"; reverting a cell to its original removes the record. The
// journal is what SaveEdits persists (editsPayload.ts) and what drives the grid's edited
// markers and the review step's edit count. Dies on refresh with the rest of the run data.

import { cellKey } from "./validationResultStore";

export type CellEditRecord = {
  worksheet: string;
  rowNumber: number;
  field: string;
  originalValue: string;
  newValue: string;
  editedAt: string;
};

const editsBySubmission = new Map<string, Map<string, CellEditRecord>>();

export function recordCellEdit(
  submissionID: string,
  edit: { worksheet: string; rowNumber: number; field: string; currentValue: string; newValue: string }
): void {
  let edits = editsBySubmission.get(submissionID);
  if (!edits) {
    edits = new Map();
    editsBySubmission.set(submissionID, edits);
  }

  const key = cellKey(edit.worksheet, edit.rowNumber, edit.field);
  const existing = edits.get(key);
  const originalValue = existing ? existing.originalValue : edit.currentValue;

  if (edit.newValue === originalValue) {
    edits.delete(key);
    return;
  }

  edits.set(key, {
    worksheet: edit.worksheet,
    rowNumber: edit.rowNumber,
    field: edit.field,
    originalValue,
    newValue: edit.newValue,
    editedAt: new Date().toISOString(),
  });
}

export function getCellEdits(submissionID: string): Map<string, CellEditRecord> {
  return editsBySubmission.get(submissionID) ?? new Map();
}

export function getCellEditCount(submissionID: string): number {
  return editsBySubmission.get(submissionID)?.size ?? 0;
}

// A re-uploaded corrected file supersedes the journal — edits described the old file.
export function clearCellEdits(submissionID: string): void {
  editsBySubmission.delete(submissionID);
}

// GetEdits hydration on resume: replaces the (dead-on-refresh) journal with the persisted
// records, insertion order preserved so a later SaveEdits reproduces the same sequence.
export function hydrateCellEdits(submissionID: string, records: CellEditRecord[]): void {
  const edits = new Map<string, CellEditRecord>();
  for (const record of records) {
    edits.set(cellKey(record.worksheet, record.rowNumber, record.field), record);
  }
  editsBySubmission.set(submissionID, edits);
}
